import { useEffect, useState } from "react";
import {
  Card, CardHeader, CardBody, CardFooter,
  Container, Row, Col, Input, Label, Button,
  Table, Pagination, PaginationItem, PaginationLink
} from "reactstrap";
import axios from "axios";
import Header from "components/Headers/Header";
import Loader from "components/Loader";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer
} from "recharts";

const API_URL = process.env.REACT_APP_API_URL;

const AdManagerDomainComparativo = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortColumn, setSortColumn] = useState("revenue");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 🔧 Função de formatação robusta
  const formatDate = (input) => {
    if (!input) return "";
    let cleaned = input.toString().trim();

    // Se vier no formato 20251106 → transforma
    if (/^\d{8}$/.test(cleaned)) {
      const y = cleaned.substring(0, 4);
      const m = cleaned.substring(4, 6);
      const d = cleaned.substring(6, 8);
      return `${d}/${m}/${y}`;
    }

    // Se vier no formato 2025-11-06 → transforma
    if (/^\d{4}-\d{2}-\d{2}$/.test(cleaned)) {
      const [y, m, d] = cleaned.split("-");
      return `${d}/${m}/${y}`;
    }

    // Se já estiver no formato DD/MM/YYYY → mantém
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(cleaned)) return cleaned;

    // Fallback — tenta criar uma data válida
    const dateObj = new Date(cleaned);
    if (!isNaN(dateObj)) {
      const d = String(dateObj.getDate()).padStart(2, "0");
      const m = String(dateObj.getMonth() + 1).padStart(2, "0");
      const y = dateObj.getFullYear();
      return `${d}/${m}/${y}`;
    }

    return cleaned;
  };

  // 🚀 Buscar dados combinados
  const fetchData = async (custom = false) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const body =
        custom && startDate && endDate
          ? { startDate, endDate }
          : { relative: "YESTERDAY" };

      // 📊 1. Ad Manager
      const adManagerRes = await axios.post(`${API_URL}/admanager/report`, body, { headers });
      const adRows = adManagerRes.data.rows || [];

      const adData = {};
      adRows.forEach((item) => {
        const domain = item.adUnit || "(não identificado)";
        const date = formatDate(item.date || new Date().toISOString().split("T")[0]);
        if (!adData[domain]) {
          adData[domain] = {
            domain,
            date,
            impressions: 0,
            clicks: 0,
            revenue: 0,
            ecpm: 0,
            unfilled: 0,
            count: 0,
          };
        }
        adData[domain].impressions += Number(item.impressions || 0);
        adData[domain].clicks += Number(item.clicks || 0);
        adData[domain].revenue += Number(item.revenue || 0);
        adData[domain].ecpm += Number(item.eCPM || 0);
        adData[domain].unfilled += Number(item.unfilledImpressions || 0);
        adData[domain].count++;
      });

      // 📈 2. Google Analytics
      const analyticsRes = await axios.post(`${API_URL}/analytics/domain`, body, { headers });
      const analyticsRows = analyticsRes.data || [];

      const combined = {};

      analyticsRows.forEach((a) => {
        const key = a.host?.replace("www.", "") || "(vazio)";
        if (!combined[key]) combined[key] = { domain: key };
        combined[key].sessions = Number(a.sessions || 0);
        combined[key].users = Number(a.activeUsers || 0);
        combined[key].events = Number(a.events || 0);
      });

      Object.values(adData).forEach((ad) => {
        const key = ad.domain.replace("www.", "");
        if (!combined[key]) combined[key] = { domain: key };
        combined[key].date = ad.date;
        combined[key].revenue = Number(ad.revenue || 0);
        combined[key].impressions = Number(ad.impressions || 0);
        combined[key].clicks = Number(ad.clicks || 0);
        combined[key].ecpm = ad.count > 0 ? ad.ecpm / ad.count : 0;
        combined[key].ctr =
          ad.impressions > 0 ? (ad.clicks / ad.impressions) * 100 : 0;
      });

      // 📊 3. Formatar resultados finais
      const mergedData = Object.values(combined).map((d) => ({
        ...d,
        date: formatDate(d.date || new Date().toISOString().split("T")[0]),
        revenue: Number((d.revenue || 0).toFixed(1)),
        impressions: Number((d.impressions || 0).toFixed(1)),
        clicks: Number((d.clicks || 0).toFixed(1)),
        ecpm: Number((d.ecpm || 0).toFixed(1)),
        ctr: Number((d.ctr || 0).toFixed(1)),
        sessions: Number((d.sessions || 0).toFixed(1)),
        users: Number((d.users || 0).toFixed(1)),
        events: Number((d.events || 0).toFixed(1)),
      }));

      setData(mergedData);
    } catch (err) {
      console.error("❌ Erro ao buscar dados combinados:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(false);
  }, []);

  // 🔄 Ordenação
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const sortedData = [...data].sort((a, b) => {
    const valA =
      sortColumn === "date"
        ? new Date(a.date.split("/").reverse().join("-"))
        : Number(a[sortColumn]) || 0;
    const valB =
      sortColumn === "date"
        ? new Date(b.date.split("/").reverse().join("-"))
        : Number(b[sortColumn]) || 0;
    return sortDirection === "asc" ? valA - valB : valB - valA;
  });

  // 🔢 Paginação
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const paginatedData = sortedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // 📊 Gráfico — Top 5 domínios por receita
  const chartData = [...data]
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 5)
    .reverse();

  return (
    <>
      <Header />
      <Container className="mt--7" fluid>
        {/* 🔍 Filtros */}
        <Row className="mb-4">
          <Col md="3">
            <Label>Início:</Label>
            <Input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Col>
          <Col md="3">
            <Label>Fim:</Label>
            <Input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Col>
          <Col md="3" className="d-flex align-items-end">
            <Button color="primary" onClick={() => fetchData(true)}>
              🔍 Consultar
            </Button>
          </Col>
        </Row>

        {/* 📊 Gráfico de Receita x Sessões */}
        <Row>
          <Col xl="12">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">💰 Receita x Sessões por Domínio (Top 5)</h3>
              </CardHeader>
              <CardBody
                style={{
                  height: 300,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {loading ? (
                  <Loader />
                ) : chartData.length === 0 ? (
                  <p className="text-center text-muted">Sem dados disponíveis.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={chartData}>
                      <XAxis dataKey="domain" />
                      <YAxis yAxisId="left" stroke="#8884d8" />
                      <YAxis yAxisId="right" orientation="right" stroke="#00C49F" />
                      <Tooltip formatter={(v) => v.toFixed(1)} />
                      <Legend />
                      <Bar yAxisId="left" dataKey="sessions" fill="#8884d8" name="Sessões" />
                      <Bar yAxisId="right" dataKey="revenue" fill="#00C49F" name="Receita (USD)" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* 📋 Tabela Detalhada */}
        <Row className="mt-4">
          <Col xl="12">
            <Card className="shadow">
              <CardHeader>
                <h3 className="mb-0">📋 Detalhamento por Domínio</h3>
              </CardHeader>
              {loading ? (
                <Loader />
              ) : (
                <>
                  <CardBody>
                    <Table responsive className="table-flush">
                      <thead className="thead-light">
                        <tr>
                          {[
                            { key: "date", label: "📅 Data" },
                            { key: "domain", label: "🌐 Domínio" },
                            { key: "users", label: "👤 Usuários" },
                            { key: "sessions", label: "📊 Sessões" },
                            { key: "impressions", label: "👁️ Impressões" },
                            { key: "clicks", label: "🖱️ Cliques" },
                            { key: "ctr", label: "CTR (%)" },
                            { key: "revenue", label: "💰 Receita (USD)" },
                            { key: "ecpm", label: "📈 eCPM (USD)" },
                          ].map((col) => (
                            <th
                              key={col.key}
                              style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                              onClick={() => handleSort(col.key)}
                            >
                              {col.label}{" "}
                              {sortColumn === col.key
                                ? sortDirection === "asc"
                                  ? "▲"
                                  : "▼"
                                : ""}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((row, idx) => (
                          <tr key={idx}>
                            <td>{row.date}</td>
                            <td>{row.domain}</td>
                            <td>{row.users}</td>
                            <td>{row.sessions}</td>
                            <td>{row.impressions}</td>
                            <td>{row.clicks}</td>
                            <td>{row.ctr.toFixed(1)}%</td>
                            <td>${row.revenue.toFixed(1)}</td>
                            <td>${row.ecpm.toFixed(1)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </CardBody>

                  {/* 🔢 Paginação */}
                  <CardFooter className="py-4">
                    <Pagination className="pagination justify-content-end mb-0">
                      <PaginationItem disabled={currentPage === 1}>
                        <PaginationLink href="#" onClick={(e) => {
                          e.preventDefault();
                          if (currentPage > 1) setCurrentPage(currentPage - 1);
                        }}>
                          <i className="fas fa-angle-left" />
                        </PaginationLink>
                      </PaginationItem>

                      {Array.from({ length: totalPages }, (_, i) => (
                        <PaginationItem key={i} active={currentPage === i + 1}>
                          <PaginationLink href="#" onClick={(e) => {
                            e.preventDefault();
                            setCurrentPage(i + 1);
                          }}>
                            {i + 1}
                          </PaginationLink>
                        </PaginationItem>
                      ))}

                      <PaginationItem disabled={currentPage === totalPages}>
                        <PaginationLink href="#" onClick={(e) => {
                          e.preventDefault();
                          if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                        }}>
                          <i className="fas fa-angle-right" />
                        </PaginationLink>
                      </PaginationItem>
                    </Pagination>
                  </CardFooter>
                </>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default AdManagerDomainComparativo;